import { notify, useModal } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Account } from '@modules-deprecated/app/accounts/types'

import { ModalId } from '../../../../../../../../../enums/modalId'
import { NotificationKeys } from '../../../../../../../../../enums/notificationKeys'
import { APIError } from '../../../../../../../../../utils'
import { useCreateAccountFromTemplate } from '../../../../../hooks/useCreateAccountFromTemplate'
import { CreateAccountFromTemplatePayload } from '../../../../../query-api'
import { getAccountNumberValidationMessage } from '../../../utils/getAccountNumberValidationMessage'

type PendingAccount = Omit<CreateAccountFromTemplatePayload, 'organizationId'>

interface UseCreateAccountFromTemplateSubmissionOptions {
  onAccountNumberConflict: () => void
  onAccountNumberError: (message: string) => void
  onClose?: () => void
  onReset: () => void
  onSave?: (account: Account) => void
  organizationId: string | undefined
}

export const useCreateAccountFromTemplateSubmission = (options: UseCreateAccountFromTemplateSubmissionOptions) => {
  const { onAccountNumberConflict, onAccountNumberError, onClose, onReset, onSave, organizationId } = options
  const [pendingAccount, setPendingAccount] = useState<PendingAccount | null>(null)
  const { t } = useTranslation()
  const { close: closeCreateModal } = useModal(ModalId.AccountFromTemplateModalCreate)
  const { close: closeConfirmationModal, open: openConfirmationModal } = useModal(
    ModalId.AccountFromTemplateModalCreateConfirmation,
  )

  const closeModal = useCallback(() => {
    if (onClose) {
      onClose()
    } else {
      closeCreateModal()
    }

    setPendingAccount(null)
    onReset()
  }, [closeCreateModal, onClose, onReset])

  const handleCreateError = useCallback(
    (error: APIError) => {
      const fieldErrorMessage = getAccountNumberValidationMessage(error)
      if (fieldErrorMessage) {
        closeConfirmationModal()
        onAccountNumberError(fieldErrorMessage)
        onAccountNumberConflict()
        return
      }

      notify({
        id: NotificationKeys.AccountFromTemplateCreate,
        message: error.body?.errorMessage || t('error_saving'),
        variant: 'error',
      })
      trackError(error)
    },
    [closeConfirmationModal, onAccountNumberConflict, onAccountNumberError, t],
  )

  const { createAccount, isProcessing } = useCreateAccountFromTemplate({
    onError: handleCreateError,
    onSuccess: async (data) => {
      await onSave?.(data.accounts[0])
      closeConfirmationModal()
      closeModal()
    },
  })

  const requestConfirmation = useCallback(
    (account: PendingAccount) => {
      setPendingAccount(account)
      openConfirmationModal()
    },
    [openConfirmationModal],
  )

  const cancelConfirmation = useCallback(() => {
    setPendingAccount(null)
  }, [])

  const confirmCreate = useCallback(() => {
    if (!organizationId || !pendingAccount) {
      return
    }

    createAccount({ ...pendingAccount, organizationId })
  }, [createAccount, organizationId, pendingAccount])

  return {
    accountNumberToConfirm: pendingAccount?.accountNumber ?? null,
    cancelConfirmation,
    closeModal,
    confirmCreate,
    isAwaitingConfirmation: pendingAccount !== null,
    isProcessing,
    requestConfirmation,
  }
}
